import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { TopNav } from "@/components/TopNav";
import { SideNav } from "@/components/SideNav";
import { ConfirmProvider, ToastProvider } from "@/components/ui";
import { SmartListProvider } from "@/features/lists/SmartListContext";
import { api } from "@/lib/api";
import type { WorkspaceSettings } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Pages that want to fill the full viewport width edge-to-edge (no max-width
 * cap, no horizontal padding) — listed by route prefix. Everything else gets
 * a comfortable centered container with breathing room.
 *
 * Contact-detail (`/contacts/:id`) is also full-bleed because its three
 * columns each manage their own scroll and the page must fit the viewport
 * exactly — pattern-matched separately so /contacts (the list) keeps the
 * standard padded layout.
 */
const FULL_BLEED_ROUTES = ["/inbox", "/pipelines", "/calendar", "/mail"];
const CONTACT_DETAIL = /^\/contacts\/[^/]+$/;
// Automation detail owns its own scrollable layout so the flow diagram
// can fill the rest of the viewport. The list view (`/automations`)
// keeps the standard padded layout.
const AUTOMATION_DETAIL = /^\/automations\/[^/]+$/;

export function CrmLayout() {
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const { pathname } = useLocation();
  const fullBleed = FULL_BLEED_ROUTES.some((p) => pathname === p || pathname.startsWith(p + "/"))
    || CONTACT_DETAIL.test(pathname)
    || AUTOMATION_DETAIL.test(pathname);

  useEffect(() => {
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => setWorkspaceName(d.settings.workspace_name || "CRM"))
      .catch(() => setWorkspaceName("CRM"));
  }, []);

  return (
    <ToastProvider>
      <ConfirmProvider>
      <SmartListProvider>
        {/* h-screen + flex-col + min-h-0 on <main> so full-bleed pages can
            use `h-full` to fill exactly the remaining viewport without
            ever computing 100vh − topnav heights manually (those calcs
            kept drifting by a few pixels because of border lines between
            the primary and secondary nav rows). Padded pages still grow
            naturally inside the same flex slot. */}
        {/* lg+: SideNav column + content column (TopNav is a slim toolbar).
            Below lg: SideNav hides and TopNav is the full navigation. */}
        <div className="flex h-screen overflow-hidden bg-zinc-950">
          <SideNav workspaceName={workspaceName ?? undefined} />
          <div className="flex min-w-0 flex-1 flex-col">
            <TopNav workspaceName={workspaceName ?? undefined} />
            <main className={cn("flex-1 min-h-0", fullBleed ? "" : "overflow-y-auto")}>
              {fullBleed ? (
                // Full-bleed: page owns its own layout (sidebars, scroll, etc.).
                <Outlet />
              ) : (
                <div className="mx-auto w-full max-w-screen-2xl px-3 py-5 sm:px-4 sm:py-6 lg:px-6">
                  <Outlet />
                </div>
              )}
            </main>
          </div>
        </div>
      </SmartListProvider>
      </ConfirmProvider>
    </ToastProvider>
  );
}
